import { signal } from '@angular/core';

import {
  CompensationTypeDto,
  FactoryCompensationDto,
  FactoryCreateProposalDto,
  FactoryManageVacationsProposalsDto,
  HolidayDto,
  TimeCompensationDetailsDto
} from '@app/data/dtos/manage-vacations/factory/factory-manage-vacations.dto';
import { SeasonCalendarPeriodDto } from '@app/data/dtos/manage-vacations/manage-vacations.dto';
import { FactoryCompensationType, SEASONS } from '@app/entities/enums/manage-vacations/manage-vacations.enum';
import {
  CompensationType,
  FactoryCompensation,
  FactoryManageVacationsProposals,
  FactorySeasonProposal,
  Holidays,
  TimeCompensationDetails
} from '@app/entities/models/manage-vacations/factory-manage-vacations.model';
import { MotFactoryProposal } from '@app/entities/models/manage-vacations/mot-manage-vacations.model';

import { MapFromFn } from '@mercadona/common/public';
import { Builder } from '@mercadona/common/utils';

import { mapMotFactoryPeriods } from '../mot/mot-manage-vacations.mapper';

import { AnualCalendarDayType, AnualCalendarMonth } from '@/models/lababs/lababs-vacations.model';
import { SeasonCalendar, SeasonCalendarDayType } from '@/models/manage-vacations/manage-vacations.model';

export const mapFactoryVacationsProposals: MapFromFn<
  FactoryManageVacationsProposalsDto,
  FactoryManageVacationsProposals
> = (value: FactoryManageVacationsProposalsDto): FactoryManageVacationsProposals => {
  const winterProposals = value?.winter_proposals;
  const summerProposals = value?.summer_proposals;

  return Builder.forModel(FactoryManageVacationsProposals)
    .winter(
      winterProposals?.length
        ? mapFactorySeasonProposal(winterProposals.map(mapMotFactoryPeriods))
        : initDefaultFactorySeasonProposalBySeason(SEASONS.WINTER)
    )
    .summer(
      summerProposals?.length
        ? mapFactorySeasonProposal(summerProposals.map(mapMotFactoryPeriods))
        : initDefaultFactorySeasonProposalBySeason(SEASONS.SUMMER)
    )
    .observations(value?.observations ?? '')
    .titleMessage(value?.vacation_proposal_message ?? '')
    .build();
};

export const initDefaultFactorySeasonProposalBySeason = (season: SEASONS): FactorySeasonProposal => {
  const defaultProposal = new FactorySeasonProposal();
  defaultProposal.setValues(season);
  return defaultProposal;
};

export const mapFactorySeasonProposal: MapFromFn<MotFactoryProposal[], FactorySeasonProposal> = (value) =>
  Builder.forModel(FactorySeasonProposal).proposals(signal(value)).build();

export const mapFactoryCreateProposalDto = (
  proposals: FactoryManageVacationsProposals,
  storedProposals: MotFactoryProposal[],
  isSummerSeason: boolean
): FactoryCreateProposalDto => {
  const createProposal: FactoryCreateProposalDto = {
    winter_proposals: [],
    summer_proposals: [],
    employee_observations: ''
  };

  if (isSummerSeason) {
    createProposal.winter_proposals = proposals?.winter.proposals().map(mapFactoryProposalDto) || [];
    createProposal.summer_proposals = storedProposals.map(mapFactoryProposalDto);
  } else {
    createProposal.winter_proposals = storedProposals.map(mapFactoryProposalDto);
    createProposal.summer_proposals = proposals?.summer.proposals().map(mapFactoryProposalDto) || [];
  }

  createProposal.employee_observations = proposals?.observations || '';

  return createProposal;
};

export const mapFactoryProposalDto: MapFromFn<MotFactoryProposal, SeasonCalendarPeriodDto> = (value) =>
  Builder.forDto<SeasonCalendarPeriodDto>()
    .id(value.id ?? 0)
    .order(value.order ?? 1)
    .build();

export const mapFactoryCompensation: MapFromFn<FactoryCompensationDto, FactoryCompensation> = (value) =>
  Builder.forModel(FactoryCompensation)
    .compensations(value.compensations?.map(mapCompensationType) ?? [])
    .timeCompensationDetails(value.time_compensations_details?.map(mapTimeCompensationDetails) ?? [])
    .holidays(value.holidays?.map(mapHolidayDto) ?? [])
    .build();

export const mapCompensationType: MapFromFn<CompensationTypeDto, CompensationType> = (value) =>
  Builder.forModel(CompensationType)
    .type(value.type as FactoryCompensationType)
    .date(value.date)
    .build();

export const mapTimeCompensationDetails: MapFromFn<TimeCompensationDetailsDto, TimeCompensationDetails> = (value) =>
  Builder.forModel(TimeCompensationDetails).priority(value.priority).date(value.date).build();

export const mapHolidayDto: MapFromFn<HolidayDto, Holidays> = (value) =>
  Builder.forModel(Holidays).date(value.date).dateDesc(value.date_desc).dayOfWeek(value.day_of_week).build();

export const mapFactoryCompensationDto = (compensation: FactoryCompensation): FactoryCompensationDto => ({
  compensations: compensation.compensations?.map(mapCompensationTypeDto) ?? [],
  time_compensations_details: compensation.timeCompensationDetails?.map(mapTimeCompensationDetailsDto) ?? []
});

export const mapCompensationTypeDto = (compensationType: CompensationType): CompensationTypeDto => ({
  type: compensationType.type,
  date: compensationType.date
});

export const mapTimeCompensationDetailsDto = (
  timeCompensationDetails: TimeCompensationDetails
): TimeCompensationDetailsDto => ({
  priority: timeCompensationDetails.priority,
  date: timeCompensationDetails.date
});

const emptyDayType: AnualCalendarDayType = {
  ids: [],
  name: '',
  primaryColour: '',
  primaryShape: '',
  secondaryColour: '',
  secondaryShape: '',
  isWorkingDay: true
};

const mapDayType = (dayType: SeasonCalendarDayType): AnualCalendarDayType => ({
  ids: dayType.ids ?? [],
  name: dayType.name ?? '',
  primaryColour: dayType.primaryColour ?? '',
  primaryShape: dayType.primaryShape ?? '',
  secondaryColour: dayType.secondaryColour ?? '',
  secondaryShape: dayType.secondaryShape ?? '',
  isWorkingDay: dayType.isWorkingDay ?? true
});

export const mapSeasonCalendarToAnualCalendarMonths = <T>(seasonCalendar: SeasonCalendar<T>): AnualCalendarMonth[] =>
  seasonCalendar.calendar.map((month) => ({
    yearLabel: month.year,
    monthLabel: month.month,
    weeks: month.weeks.map((week) => ({
      days: week.days.map((day) => ({
        date: day.date ?? '',
        dayLabel: day.day ?? '',
        dayType: day.dayType ? mapDayType(day.dayType) : day.date ? emptyDayType : null,
        selected: false
      }))
    }))
  }));
